import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ChefHat,
  Edit3,
  Globe2,
  Hash,
  Image as ImageIcon,
  Languages,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  Store,
  Trash2,
  UserRound,
  Users,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useConfirm } from "../../components/feedback/ConfirmProvider";
import userManagementApi from "../../api/userManagementApi";
import resolveMediaUrl from "../../utils/resolveMediaUrl";
import {
  canManageUsers,
  getApiMessage,
  getUserEstablishmentId,
  getUserMode,
  roleLabels,
  translateFallback,
} from "./userManagementHelpers";
import "../../styles/users/user-details.css";

const normalizeRole = (role) => {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
};

const getManagerEstablishment = (record = {}) => {
  const safeRecord = record || {};

  return (
    safeRecord.establishment ||
    safeRecord.managed_establishment ||
    safeRecord.managedEstablishment ||
    safeRecord.restaurant ||
    safeRecord.business?.establishment ||
    safeRecord.business?.establishments?.[0] ||
    null
  );
};

const getManagerBusiness = (record = {}) => {
  const safeRecord = record || {};

  return safeRecord.business || getManagerEstablishment(safeRecord)?.business || null;
};

const getLogoUrl = (record = {}) => {
  const safeRecord = record || {};
  const establishment = getManagerEstablishment(safeRecord);

  return resolveMediaUrl(
    establishment?.logo_url ||
      establishment?.logo ||
      establishment?.logo_path ||
      safeRecord.logo_url ||
      safeRecord.logo ||
      "",
  );
};

const formatValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  return value;
};

function DetailItem({ icon: Icon, label, value, forceLtr = false }) {
  return (
    <article>
      <span>
        <Icon size={18} />
        {label}
      </span>

      <strong dir={forceLtr ? "ltr" : undefined}>
        {formatValue(value)}
      </strong>
    </article>
  );
}

function UserDetails() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { confirm } = useConfirm();

  const mode = getUserMode(user);
  const canAccess = canManageUsers(user);

  const establishmentId = useMemo(
    () => getUserEstablishmentId(user),
    [user],
  );

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [direction, setDirection] = useState(
    document.documentElement.dir || "ltr",
  );

  const isRtl = direction === "rtl";

  // Keep this page synchronized with the global language direction.
  useEffect(() => {
    const updateDirection = () => {
      setDirection(document.documentElement.dir || "ltr");
    };

    updateDirection();

    const observer = new MutationObserver(updateDirection);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["dir", "lang"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Load the requested manager or staff record.
  const loadUser = async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response =
        mode === "managers"
          ? await userManagementApi.getManager(userId)
          : await userManagementApi.getStaffUser(
              establishmentId,
              userId,
            );

      setRecord(response.data.data || response.data || null);
    } catch (error) {
      setMessage({
        type: "error",
        text: getApiMessage(
          error,
          translateFallback(
            t,
            "usersManagement.messages.loadError",
            "Unable to load users.",
          ),
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, establishmentId, userId, canAccess]);

  // Activate or deactivate the selected user.
  const toggleStatus = async () => {
    if (!record) return;

    try {
      const nextStatus = !record.is_active;

      if (mode === "managers") {
        await userManagementApi.updateManagerStatus(
          record.id,
          nextStatus,
        );
      } else {
        await userManagementApi.updateStaffStatus(
          establishmentId,
          record.id,
          nextStatus,
        );
      }

      setRecord((current) => ({
        ...current,
        is_active: nextStatus,
      }));

      setMessage({
        type: "success",
        text: translateFallback(
          t,
          "usersManagement.messages.statusUpdated",
          "User status updated successfully.",
        ),
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: getApiMessage(
          error,
          translateFallback(
            t,
            "usersManagement.messages.statusError",
            "Unable to update user status.",
          ),
        ),
      });
    }
  };

  // Remove a staff user after confirmation.
  const removeStaff = async () => {
    if (!record || mode !== "staff") return;

    const confirmed = await confirm({
      title: translateFallback(
        t,
        "common.delete",
        "Delete",
      ),
      message: translateFallback(
        t,
        "usersManagement.messages.confirmDelete",
        "Remove this staff user from the restaurant?",
      ),
      itemName: record?.name || record?.email || "",
      confirmText: translateFallback(
        t,
        "common.delete",
        "Delete",
      ),
      cancelText: translateFallback(
        t,
        "common.cancel",
        "Cancel",
      ),
      closeText: translateFallback(
        t,
        "common.close",
        "Close",
      ),
      variant: "danger",
    });

    if (!confirmed) return;

    try {
      await userManagementApi.deleteStaff(
        establishmentId,
        record.id,
      );

      navigate("/users", {
        replace: true,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: getApiMessage(
          error,
          translateFallback(
            t,
            "usersManagement.messages.deleteError",
            "Unable to remove staff user.",
          ),
        ),
      });
    }
  };

  const getRoleIcon = () => {
    if (normalizeRole(record?.role) === "cuisini") {
      return ChefHat;
    }

    if (normalizeRole(record?.role) === "manager") {
      return BriefcaseBusiness;
    }

    return Users;
  };

  const getBooleanLabel = (value) => {
    return value
      ? translateFallback(
          t,
          "usersManagement.details.enabled",
          "Enabled",
        )
      : translateFallback(
          t,
          "usersManagement.details.disabled",
          "Disabled",
        );
  };

  const getBusinessTypeLabel = (type) => {
    if (!type) return "-";

    return translateFallback(
      t,
      `usersManagement.businessTypes.${type}`,
      type,
    );
  };

  const getLanguageLabel = (locale) => {
    if (!locale) return "-";

    return translateFallback(
      t,
      `languages.${locale}`,
      locale,
    );
  };

  if (!canAccess) {
    return (
      <main
        className={`users-details-page ${
          isRtl
            ? "users-details-page--rtl"
            : "users-details-page--ltr"
        }`}
        dir={direction}
      >
        <section className="users-details-blocked">
          <ShieldCheck size={34} />

          <h1>
            {translateFallback(
              t,
              "usersManagement.blocked.title",
              "Access restricted",
            )}
          </h1>

          <p>
            {translateFallback(
              t,
              "usersManagement.blocked.description",
              "Only super admin and manager accounts can manage users.",
            )}
          </p>
        </section>
      </main>
    );
  }

  const RoleIcon = getRoleIcon();

  const managerRecord =
    normalizeRole(record?.role) === "manager" ||
    mode === "managers";

  const establishment = managerRecord
    ? getManagerEstablishment(record)
    : null;

  const business = managerRecord
    ? getManagerBusiness(record)
    : null;

  const logoUrl = managerRecord
    ? getLogoUrl(record)
    : "";

  const businessName =
    record?.business_name ||
    business?.name ||
    establishment?.name ||
    "-";

  const businessTypeCode =
    establishment?.type ||
    record?.business_type ||
    "";

  const defaultLanguageCode =
    establishment?.default_language ||
    business?.default_language ||
    "";

  return (
    <main
      className={`users-details-page ${
        isRtl
          ? "users-details-page--rtl"
          : "users-details-page--ltr"
      }`}
      dir={direction}
    >
      <header className="users-details-header">
        <button
          type="button"
          className="users-details-back-button"
          onClick={() => navigate("/users")}
        >
          {isRtl ? (
            <ArrowRight size={18} />
          ) : (
            <ArrowLeft size={18} />
          )}

          <span>
            {translateFallback(
              t,
              "common.back",
              "Back",
            )}
          </span>
        </button>

        <div className="users-details-header__copy">
          <p className="users-details-eyebrow">
            {translateFallback(
              t,
              "usersManagement.details.eyebrow",
              "User details",
            )}
          </p>

          <h1>
            {managerRecord
              ? translateFallback(
                  t,
                  "usersManagement.details.managerTitle",
                  "Manager profile",
                )
              : translateFallback(
                  t,
                  "usersManagement.details.title",
                  "User profile",
                )}
          </h1>

          <span>
            {managerRecord
              ? translateFallback(
                  t,
                  "usersManagement.details.managerDescription",
                  "Review the manager account, business and establishment information.",
                )
              : translateFallback(
                  t,
                  "usersManagement.details.description",
                  "Review account information, role and status.",
                )}
          </span>
        </div>
      </header>

      {message && (
        <div
          className={`users-details-alert users-details-alert--${message.type}`}
          role="alert"
        >
          {message.type === "success" ? (
            <CheckCircle2 size={18} />
          ) : (
            <XCircle size={18} />
          )}

          <span>{message.text}</span>
        </div>
      )}

      {loading ? (
        <section className="users-details-panel">
          <div className="users-details-loading">
            <Loader2 size={28} />

            <span>
              {translateFallback(
                t,
                "usersManagement.loading",
                "Loading users...",
              )}
            </span>
          </div>
        </section>
      ) : !record ? (
        <section className="users-details-panel">
          <div className="users-details-empty">
            <UserRound size={30} />

            <strong>
              {translateFallback(
                t,
                "usersManagement.details.notFound",
                "User not found",
              )}
            </strong>
          </div>
        </section>
      ) : (
        <section className="users-details-card">
          <div className="users-details-card__hero">
            <div
              className={`users-details-avatar ${
                managerRecord
                  ? "users-details-avatar--logo"
                  : ""
              }`}
            >
              {managerRecord && logoUrl ? (
                <img
                  src={logoUrl}
                  alt={businessName}
                />
              ) : managerRecord ? (
                <Store size={34} />
              ) : (
                <UserRound size={34} />
              )}
            </div>

            <div className="users-details-card__identity">
              <p className="users-details-eyebrow">
                {managerRecord
                  ? translateFallback(
                      t,
                      "usersManagement.details.managerAccount",
                      "Manager account",
                    )
                  : translateFallback(
                      t,
                      "usersManagement.details.account",
                      "Account",
                    )}
              </p>

              <h2>{record.name}</h2>

              <span dir="ltr">
                {record.email}
              </span>

              {managerRecord && (
                <small className="users-details-business-name">
                  {businessName}
                </small>
              )}
            </div>

            <span
              className={`users-details-status ${
                record.is_active
                  ? "users-details-status--active"
                  : "users-details-status--inactive"
              }`}
            >
              {record.is_active
                ? translateFallback(
                    t,
                    "usersManagement.status.active",
                    "Active",
                  )
                : translateFallback(
                    t,
                    "usersManagement.status.inactive",
                    "Inactive",
                  )}
            </span>
          </div>

          <div className="users-details-grid">
            <DetailItem
              icon={RoleIcon}
              label={translateFallback(
                t,
                "usersManagement.table.role",
                "Role",
              )}
              value={translateFallback(
                t,
                `usersManagement.roles.${record.role}`,
                roleLabels[record.role] || record.role,
              )}
            />

            <DetailItem
              icon={Mail}
              label={translateFallback(
                t,
                "usersManagement.form.email",
                "Email",
              )}
              value={record.email}
              forceLtr
            />

            <DetailItem
              icon={Phone}
              label={translateFallback(
                t,
                "usersManagement.form.phone",
                "Phone",
              )}
              value={record.phone}
              forceLtr
            />

            <DetailItem
              icon={Hash}
              label={translateFallback(
                t,
                "usersManagement.details.userId",
                "User ID",
              )}
              value={record.id}
            />
          </div>

          {managerRecord && (
            <>
              <section className="users-details-manager-section">
                <div className="users-details-manager-section__head">
                  <span>
                    <Store size={20} />
                  </span>

                  <div>
                    <h3>
                      {translateFallback(
                        t,
                        "usersManagement.details.businessInformation",
                        "Business information",
                      )}
                    </h3>

                    <p>
                      {translateFallback(
                        t,
                        "usersManagement.details.businessInformationDescription",
                        "Information saved in the businesses table.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="users-details-grid users-details-grid--manager">
                  <DetailItem
                    icon={Store}
                    label={translateFallback(
                      t,
                      "usersManagement.details.businessName",
                      "Business name",
                    )}
                    value={businessName}
                  />

                  <DetailItem
                    icon={Hash}
                    label={translateFallback(
                      t,
                      "usersManagement.details.businessId",
                      "Business ID",
                    )}
                    value={business?.id}
                  />

                  <DetailItem
                    icon={Globe2}
                    label={translateFallback(
                      t,
                      "usersManagement.details.businessSlug",
                      "Business slug",
                    )}
                    value={business?.slug}
                    forceLtr
                  />

                  <DetailItem
                    icon={Languages}
                    label={translateFallback(
                      t,
                      "usersManagement.details.defaultLanguage",
                      "Default language",
                    )}
                    value={getLanguageLabel(
                      defaultLanguageCode,
                    )}
                  />

                  <DetailItem
                    icon={ShieldCheck}
                    label={translateFallback(
                      t,
                      "usersManagement.details.businessStatus",
                      "Business status",
                    )}
                    value={business?.status}
                  />

                  <DetailItem
                    icon={UserRound}
                    label={translateFallback(
                      t,
                      "usersManagement.details.businessOwnerId",
                      "Business owner ID",
                    )}
                    value={
                      business?.owner_id ||
                      record.id
                    }
                  />
                </div>
              </section>

              <section className="users-details-manager-section">
                <div className="users-details-manager-section__head">
                  <span>
                    <Building2 size={20} />
                  </span>

                  <div>
                    <h3>
                      {translateFallback(
                        t,
                        "usersManagement.details.establishmentInformation",
                        "Establishment information",
                      )}
                    </h3>

                    <p>
                      {translateFallback(
                        t,
                        "usersManagement.details.establishmentInformationDescription",
                        "Restaurant details saved in the establishments table.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="users-details-grid users-details-grid--manager">
                  <DetailItem
                    icon={Building2}
                    label={translateFallback(
                      t,
                      "usersManagement.details.establishmentName",
                      "Establishment name",
                    )}
                    value={
                      establishment?.name ||
                      businessName
                    }
                  />

                  <DetailItem
                    icon={Hash}
                    label={translateFallback(
                      t,
                      "usersManagement.details.establishmentId",
                      "Establishment ID",
                    )}
                    value={
                      establishment?.id ||
                      record.establishment_id
                    }
                  />

                  <DetailItem
                    icon={Globe2}
                    label={translateFallback(
                      t,
                      "usersManagement.details.establishmentSlug",
                      "Establishment slug",
                    )}
                    value={establishment?.slug}
                    forceLtr
                  />

                  <DetailItem
                    icon={BriefcaseBusiness}
                    label={translateFallback(
                      t,
                      "usersManagement.details.type",
                      "Type",
                    )}
                    value={getBusinessTypeLabel(
                      businessTypeCode,
                    )}
                  />

                  <DetailItem
                    icon={Mail}
                    label={translateFallback(
                      t,
                      "usersManagement.details.businessEmail",
                      "Business email",
                    )}
                    value={establishment?.email}
                    forceLtr
                  />

                  <DetailItem
                    icon={Phone}
                    label={translateFallback(
                      t,
                      "usersManagement.details.businessPhone",
                      "Business phone",
                    )}
                    value={establishment?.phone}
                    forceLtr
                  />

                  <DetailItem
                    icon={MessageCircle}
                    label={translateFallback(
                      t,
                      "usersManagement.details.whatsapp",
                      "WhatsApp",
                    )}
                    value={establishment?.whatsapp}
                    forceLtr
                  />

                  <DetailItem
                    icon={ImageIcon}
                    label={translateFallback(
                      t,
                      "usersManagement.details.logoPath",
                      "Logo path",
                    )}
                    value={establishment?.logo_path}
                    forceLtr
                  />
                </div>

                <div className="users-details-text-blocks">
                  <article>
                    <span>
                      <MapPin size={18} />

                      {translateFallback(
                        t,
                        "usersManagement.details.address",
                        "Address",
                      )}
                    </span>

                    <p>
                      {formatValue(
                        establishment?.address,
                      )}
                    </p>
                  </article>

                  <article>
                    <span>
                      <BriefcaseBusiness size={18} />

                      {translateFallback(
                        t,
                        "usersManagement.details.descriptionLabel",
                        "Description",
                      )}
                    </span>

                    <p>
                      {formatValue(
                        establishment?.description,
                      )}
                    </p>
                  </article>
                </div>
              </section>

              <section className="users-details-manager-section">
                <div className="users-details-manager-section__head">
                  <span>
                    <Settings2 size={20} />
                  </span>

                  <div>
                    <h3>
                      {translateFallback(
                        t,
                        "usersManagement.details.publicMenuSettings",
                        "Public menu settings",
                      )}
                    </h3>

                    <p>
                      {translateFallback(
                        t,
                        "usersManagement.details.publicMenuSettingsDescription",
                        "Ordering and interaction settings linked to this manager establishment.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="users-details-grid users-details-grid--manager">
                  <DetailItem
                    icon={ShoppingBag}
                    label={translateFallback(
                      t,
                      "usersManagement.details.ordering",
                      "Ordering",
                    )}
                    value={getBooleanLabel(
                      establishment?.ordering_enabled,
                    )}
                  />

                  <DetailItem
                    icon={MessageCircle}
                    label={translateFallback(
                      t,
                      "usersManagement.details.comments",
                      "Comments",
                    )}
                    value={getBooleanLabel(
                      establishment?.allow_comments,
                    )}
                  />

                  <DetailItem
                    icon={CheckCircle2}
                    label={translateFallback(
                      t,
                      "usersManagement.details.ratings",
                      "Ratings",
                    )}
                    value={getBooleanLabel(
                      establishment?.allow_ratings,
                    )}
                  />

                  <DetailItem
                    icon={CheckCircle2}
                    label={translateFallback(
                      t,
                      "usersManagement.details.likes",
                      "Likes",
                    )}
                    value={getBooleanLabel(
                      establishment?.allow_likes,
                    )}
                  />

                  <DetailItem
                    icon={BriefcaseBusiness}
                    label={translateFallback(
                      t,
                      "usersManagement.details.menuTemplateId",
                      "Menu template ID",
                    )}
                    value={
                      establishment?.menu_template_id
                    }
                  />

                  <DetailItem
                    icon={ShieldCheck}
                    label={translateFallback(
                      t,
                      "usersManagement.details.establishmentStatus",
                      "Establishment status",
                    )}
                    value={
                      establishment?.is_active ===
                      undefined
                        ? "-"
                        : establishment.is_active
                          ? translateFallback(
                              t,
                              "usersManagement.status.active",
                              "Active",
                            )
                          : translateFallback(
                              t,
                              "usersManagement.status.inactive",
                              "Inactive",
                            )
                    }
                  />
                </div>
              </section>
            </>
          )}

          <div className="users-details-actions">
            <button
              type="button"
              className="users-details-primary-button"
              onClick={() =>
                navigate(
                  `/users/${record.id}/edit`,
                )
              }
            >
              <Edit3 size={18} />

              <span>
                {translateFallback(
                  t,
                  "usersManagement.actions.edit",
                  "Edit user",
                )}
              </span>
            </button>

            <button
              type="button"
              className="users-details-secondary-button"
              onClick={toggleStatus}
            >
              {record.is_active ? (
                <XCircle size={18} />
              ) : (
                <CheckCircle2 size={18} />
              )}

              <span>
                {record.is_active
                  ? translateFallback(
                      t,
                      "usersManagement.actions.deactivate",
                      "Deactivate",
                    )
                  : translateFallback(
                      t,
                      "usersManagement.actions.activate",
                      "Activate",
                    )}
              </span>
            </button>

            {mode === "staff" && (
              <button
                type="button"
                className="users-details-danger-button"
                onClick={removeStaff}
              >
                <Trash2 size={18} />

                <span>
                  {translateFallback(
                    t,
                    "usersManagement.actions.remove",
                    "Remove",
                  )}
                </span>
              </button>
            )}
          </div>
        </section>
      )}
    </main>
  );
}

export default UserDetails;